import {Component, Input, OnChanges} from '@angular/core';
import {DataType, FormPage, InputField} from '@models/sidebar/response/form-page';
import {FormPageService} from '@services/left-menu/page/form-page/form-page.service';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {LastSubmittedRowModalComponent} from '@components/pages/page-components/form-page/modal/last-submitted-row-modal/last-submitted-row-modal.component';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {ConfirmationDialogService} from '@services/confirmation-dialog.service';
import {ModalService} from '@services/modal/modal.service';

@Component({
    selector: 'scitis-form-page',
    templateUrl: './form-page.component.html',
    styleUrls: ['./form-page.component.scss'],
    standalone: false
})
export class FormPageComponent implements OnChanges {

  @Input()
  formPageConfiguration: FormPage = new FormPage();
  @Input()
  machineId: string = '';

  shouldDisableSubmitButton: boolean = true;
  fallBackMessage: string = '';
  clearFormTrigger: boolean = false;

  private CURRENT_PAGE_NAME = 'FormPage';

  constructor(private formPageService: FormPageService,
              private notificationService: NotificationService,
              private translateService: TranslateService,
              private modalService: ModalService,
              private loadingIndicatorService: LoadingIndicatorService,
              private confirmationDialogService: ConfirmationDialogService) {
    this.fallBackMessage = this.translateService.instant('No form page configured. Go ahead and configure one!');
    this.setDisableButton();
  }

  get formPageNotConfigured(): boolean {
    return !this.formPageConfiguration || !this.formPageConfiguration.inputFields || this.formPageConfiguration.inputFields.length === 0;
  }

  private static isFieldInput(inputField: InputField): boolean {
    return !inputField.list && inputField.datatype !== DataType.BOOLEAN;
  }

  private static isInvalidCheckbox(inputField: InputField): boolean {
    return inputField.datatype === DataType.BOOLEAN && inputField.required && (FormPageComponent.isValueNotSet(inputField) || inputField.value === 'false');
  }

  private static isInvalidList(inputField: InputField): boolean {
    return inputField.list && (!inputField.valueList || inputField.valueList.length === 0);
  }

  private static isValueNotSet(inputField: InputField): boolean {
    return !inputField.value || inputField.value.isNullOrWhitespace();
  }

  onInputFieldChanged() {
    this.setDisableButton();
  }

  ngOnChanges() {
    this.setDisableButton();
  }

  onSubmit() {
    if (this.isInvalidFormPageConfiguration()) {
      return;
    }
    const submitTitle = this.translateService.instant('Submit form');
    const submitBody = this.translateService.instant('Do you really want to submit the form?');
    const submitOk = this.translateService.instant('Submit');
    this.confirmationDialogService.handleConfirmationDialog(submitTitle, submitBody, '', submitOk).subscribe(userConfirmed => {
      if (!userConfirmed) {
        return;
      }
      this.loadingIndicatorService.show();
      this.formPageService.submit(this.machineId, this.formPageConfiguration).subscribe(() => {
        this.onClearForm();
        const successMessage = this.translateService.instant('Successfully submitted form');
        this.notificationService.success(successMessage, this.CURRENT_PAGE_NAME, false);
        this.loadingIndicatorService.hide();
      }, (error) => {
        const errorMessage = this.translateService.instant('There was an error submitting the form');
        this.notificationService.error(errorMessage, error, this.CURRENT_PAGE_NAME, true);
        this.loadingIndicatorService.hide();
      });
    }, (error) => {
      console.log(error);
    });
  }

  onShowLastSubmittedRow() {
    this.formPageService.getLastSubmittedRow(this.machineId, this.formPageConfiguration.id).subscribe((lastRow: any) => {
      const lastRowMap: Map<string, any> = new Map<string, any>();
      for (const row in lastRow) {
        lastRowMap.set(row, lastRow[row]);
      }
      this.openLastRowModal(lastRowMap);
    }, (error) => {
      const errorMessage = this.translateService.instant('There was an error getting the last submitted row');
      this.notificationService.error(errorMessage, error, this.CURRENT_PAGE_NAME, true);
    });
  }

  onClearForm() {
    if (!this.formPageConfiguration.inputFields) {
      return;
    }
    for (const inputField of this.formPageConfiguration.inputFields) {
      inputField.valueList = [];
      inputField.value = '';
    }
    this.clearFormTrigger = !this.clearFormTrigger;
    this.setDisableButton();
  }

  private openLastRowModal(lastRow: Map<string, any>) {
    const modalRef = this.modalService.open(LastSubmittedRowModalComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.lastRow = lastRow;
    modalRef.result.then((result: Map<string, any>) => {
      if (result.has('timestamp')) {
        this.loadingIndicatorService.show();
        this.deleteLastRow(result.get('timestamp') as number);
      }
    }, () => {
      this.loadingIndicatorService.hide();
    });
  }

  private deleteLastRow(timestamp: number) {
    this.formPageService.deleteLastSubmittedRow(this.machineId, this.formPageConfiguration.id, timestamp).subscribe(() => {
      const successMessage = this.translateService.instant('Successfully deleted last submitted row');
      this.notificationService.success(successMessage, this.CURRENT_PAGE_NAME, false);
      this.loadingIndicatorService.hide();
    }, (error) => {
      const errorMessage = this.translateService.instant('There was an error deleting the last submitted row');
      this.notificationService.error(errorMessage, error, this.CURRENT_PAGE_NAME, true);
      this.loadingIndicatorService.hide();
    });
  }

  private setDisableButton() {
    this.shouldDisableSubmitButton = this.isInvalidFormPageConfiguration();
  }

  private isInvalidFormPageConfiguration() {
    if (!this.formPageConfiguration.inputFields || this.formPageConfiguration.inputFields.length === 0) {
      return true;
    }
    for (const inputField of this.formPageConfiguration.inputFields) {
      if (!inputField.required) {
        continue;
      }
      if (FormPageComponent.isFieldInput(inputField) && FormPageComponent.isValueNotSet(inputField)) {
        return true;
      }
      if (FormPageComponent.isInvalidCheckbox(inputField)) {
        return true;
      }
      if (FormPageComponent.isInvalidList(inputField)) {
        return true;
      }
    }
    return false;
  }

}
